import { gl } from '../../Engine';

/**
 * Vertex Buffer Object (VBO) is a buffer that holds vertex data,\
 * such as positions, normals, texture coordinates, colors, etc in GPU memory.\
 * It serves as a container allowing efficient storage, access and rendering of vertex attributes.
 *
 * Once the VBO is set up, you can use it for rendering by binding the VBO and issuing the appropriate draw calls.\
 * WebGL will fetch the vertex data from the VBO and pass it to the shader program for processing.
 *
 * To use VertexBufferObjects user should first create VBO using {@link VertexBufferObject VertexBufferObject}-constructor.\
 * Then bind it by calling {@link bind}.\
 * Then store data on that VBO by using {@link storeData}-method.\
 * Lastly user should {@link unbind} the VBO to prevent subsequent WebGL calls from modifying this buffer.
 *
 * When the data is needed, user may bind the VertexBufferObject again by {@link bind binding} it.
 * Then after the rendering has finished, user should {@link unbind} the buffer.
 *
 * If using {@link VertexArrayObject}s when rendering, VBOs don't need to be {@link bind bound} individually.
 *
 * Once the VertexBufferObject is no longer needed, it should be deallocated from GPU memory by calling
 * {@link VertexBufferObject.delete() delete()}.
 */
export class VertexBufferObject {
    /**
     * The type of this VertexBufferObject. Should be either\
     * {@link gl.ARRAY_BUFFER} for vertex data such as vertex coordinates, vertex normals and texture coordinates or\
     * {@link gl.ELEMENT_ARRAY_BUFFER} for indices.\
     * Other types are also possible but a lot less common. For exhaustive list see {@link https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/bufferData}
     */
    protected readonly type: number;

    /**
     * Reference to {@link WebGLBuffer}.
     * It is important to remember to {@link delete} the buffer when no more needed.
     */
    protected readonly id: WebGLBuffer;

    /**
     * Constructor that will generate WebGL buffer
     * @throws Error if buffer creation fails. This could be caused by WebGL running out of memory or hitting the limit of maximum Buffers.
     * @param type The type of this VertexBufferObject. Either {@link gl.ARRAY_BUFFER} or {@link gl.ELEMENT_ARRAY_BUFFER},
     * {@link gl.ARRAY_BUFFER} for buffers storing vertex data,
     * {@link gl.ELEMENT_ARRAY_BUFFER} for buffers storing mesh index data.
     */
    public constructor(type: number) {
        const id = gl.createBuffer();
        if (!id) {
            throw new Error('VertexBufferObject creation failed');
        }
        this.id = id;
        this.type = type;
    }

    /**
     * Method to bind this {@link VertexBufferObject}.
     * This will {@link gl.bindBuffer bind the underlying WebGLBuffer} and all the subsequent buffer calls will be directed to that buffer.
     * Once done working with the buffer it shall be {@link unbind unbind}.
     * @param bindTarget the Target to which this {@link VertexBufferObject} is to be bound.\
     * Default value is {@link type}, but optionally user may define other bind targets as well.\
     * For an exhaustive list see {@link https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/bindBuffer}
     * @returns True to indicate that the buffer was successfully bound
     */
    public bind(bindTarget: number = this.type): boolean {
        gl.bindBuffer(bindTarget, this.id);
        return true;
    }

    /**
     * Method to unbind this VertexBufferObject.
     * This will bind null buffer. So any subsequent buffer calls will be discarded until another VertexBufferObject is {@link bind bound}.
     * @param bindTarget the Target from which this {@link VertexBufferObject} is to be unbound.\
     * Default value is {@link type}, but optionally user may define other bind targets as well.\
     * For an exhaustive list see {@link https://developer.mozilla.org/en-US/docs/Web/API/WebGLRenderingContext/bindBuffer}
     * @returns True to indicate that the buffer was successfully unbound
     */
    public unbind(bindTarget: number = this.type): boolean {
        gl.bindBuffer(bindTarget, null);
        return true;
    }

    /**
     * Method to store data into this VertexBufferObject.
     * In order to store data to a VertexBufferObject, the buffer must first be bound by calling {@link bind VertexBufferObject.bind()}.
     * And after the data has been stored, and the Buffer is no more modified, it should be {@link unbind unbound}.
     *
     * @param data The data to be stored within this VertexBufferObject.
     * @param usage Flag that defines the main usage of the VertexBufferObject. The possible values are:
     * gl.STATIC_DRAW, gl.DYNAMIC_DRAW, gl.STREAM_DRAW or READ/COPY derivatives. Defaults to gl.STATIC_DRAW.
     * @returns True to indicate that the data has been successfully stored
     */
    public storeData(
        data: DataView | ArrayBuffer,
        usage: number = gl.STATIC_DRAW
    ): boolean {
        gl.bufferData(this.type, data, usage);
        return true;
    }

    /**
     * Method to delete the buffer from WebGL memory.
     * This should be called whenever the buffer is no longer needed.
     * @returns True to indicate that the buffer was successfully deleted
     */
    public delete(): boolean {
        gl.deleteBuffer(this.id);
        return true;
    }
}
